import { Layout, Dropdown, Modal } from "antd";
import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Building2, ChevronDown, ChevronRight, LogOut, Repeat, Settings } from "lucide-react";
import { getDomainUrl, getLocalStorage, getUserRole, setLocalStorage } from "../utils/helpers";
import { AN_COLORS, AN_FONT } from "../design-system/tokens";
import logoPng from "../assets/logo.png";
import logoDarkPng from "../assets/logo-dark.png";
import { useAnTheme } from "../theme/AnThemeProvider";

const { Header: AntHeader } = Layout;

function Header() {
  const { mode: themeMode } = useAnTheme();
  const navigate = useNavigate();
  const userRole = getUserRole() || "";
  const [layoutView, setLayoutView] = useState(
    () => getLocalStorage("layoutView") || (userRole ? userRole.toLowerCase() : null)
  );
  const isAdmin = useMemo(
    () => userRole && userRole.toLowerCase() === "admin",
    [userRole]
  );
  const isAdminView = layoutView === "admin";


  const handleToggle = useCallback(() => {
    const newLayoutView = isAdminView ? "enduser" : "admin";
    setLayoutView(newLayoutView);
    setLocalStorage("layoutView", newLayoutView);
    navigate(newLayoutView === "admin" ? "/" : "/tenant/users/dashboard");
  }, [isAdminView, navigate]);




  const userName = localStorage.getItem("UserName") || "";
  const displayName = userName.split("@")[0] || "User";

  // Role-based avatar treatment — Admin = amber, End-user = blue. Soft/tinted
  // style: light fill, colored ring, outline icon. Reflects the current
  // effective view so toggling roles re-colors the avatar in step with the
  // switch-role menu item.
  const ROLE_ADMIN_COLOR = AN_COLORS.warn; // var(--an-warn) amber
  const ROLE_USER_COLOR = AN_COLORS.primary; // var(--an-primary) blue
  const roleColor = isAdminView ? ROLE_ADMIN_COLOR : ROLE_USER_COLOR;
  const roleBg = isAdminView ? "var(--an-warnBg)" : AN_COLORS.primaryL; // light tints
  const roleLabel = isAdminView ? "Admin" : "End-user";

  // Initials on the role colour (amber Admin, blue End-user): the same
  // avatar in the header button and on the account card.
  const initials =
    (displayName.match(/[A-Za-z0-9]+/g) || [])
      .slice(0, 2)
      .map((w) => w[0])
      .join("")
      .toUpperCase()
      .slice(0, 2) || "U";
  const renderAvatar = (size) => (
    <span
      style={{
        width: size,
        height: size,
        borderRadius: "50%",
        background: roleColor,
        color: "#fff",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        fontFamily: AN_FONT,
        fontWeight: 600,
        fontSize: Math.round(size * 0.4),
        letterSpacing: 0.3,
        boxShadow: `0 0 0 ${size >= 48 ? 4 : 2}px ${AN_COLORS.surface}`,
        userSelect: "none",
      }}
      aria-hidden="true"
    >
      {initials}
    </span>
  );

  const confirmLogout = useCallback(() => {
    Modal.confirm({
      title: "Log out?",
      content: "Are you sure you want to log out?",
      okText: "OK",
      cancelText: "Cancel",
      onOk: () => navigate("/logout"),
    });
  }, [navigate]);

  // The account menu, laid out like a browser profile card: who you are on
  // top, then what you can do. Settings lives here rather than in every
  // product's sidebar — it's the same page whatever the MFA mode.
  const [accountOpen, setAccountOpen] = useState(false);
  const tenantHost = (getDomainUrl() || "").replace(/^https?:\/\//, "").replace(/\/.*$/, "");

  const AccountItem = ({ icon, label, hint, onClick, danger }) => (
    <button
      type="button"
      onClick={() => {
        setAccountOpen(false);
        onClick();
      }}
      className="an-account-item"
      style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        width: "100%",
        padding: "10px 14px",
        border: "none",
        background: "transparent",
        borderRadius: 8,
        cursor: "pointer",
        fontFamily: AN_FONT,
        fontSize: 13.5,
        color: danger ? AN_COLORS.danger : AN_COLORS.ink,
        textAlign: "left",
      }}
    >
      <span style={{ display: "inline-flex", color: danger ? AN_COLORS.danger : AN_COLORS.ink2 }}>{icon}</span>
      <span style={{ flex: 1, minWidth: 0 }}>
        {label}
        {hint && <span style={{ display: "block", fontSize: 11.5, color: AN_COLORS.muted, marginTop: 1 }}>{hint}</span>}
      </span>
      {!danger && <ChevronRight size={14} color={AN_COLORS.muted} />}
    </button>
  );

  const accountPanel = (
    <div
      style={{
        width: 320,
        background: AN_COLORS.surface,
        border: `1px solid ${AN_COLORS.line}`,
        borderRadius: 14,
        boxShadow: "0 12px 32px rgba(17, 24, 39, 0.14)",
        padding: 12,
        fontFamily: AN_FONT,
      }}
    >
      {/* Who you are: role and tenant strip, then the identity card. */}
      <div style={{ borderRadius: 10, overflow: "hidden", border: `1px solid ${AN_COLORS.line}` }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 8,
            padding: "8px 12px",
            background: AN_COLORS.surface,
            fontSize: 12,
            color: AN_COLORS.ink2,
          }}
        >
          <span style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }} title={tenantHost}>
            {tenantHost || "This tenant"}
          </span>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6, color: roleColor, fontWeight: 600, whiteSpace: "nowrap" }}>
            {roleLabel} account <Building2 size={14} />
          </span>
        </div>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 6,
            padding: "18px 12px 16px",
            background: roleBg,
            textAlign: "center",
          }}
        >
          {renderAvatar(64)}
          <div style={{ fontSize: 16, fontWeight: 600, color: AN_COLORS.ink, marginTop: 4, maxWidth: 270, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {displayName}
          </div>
          <div style={{ fontSize: 12.5, color: AN_COLORS.ink2, maxWidth: 270, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {userName}
          </div>
        </div>
      </div>

      {/* What you can do. */}
      <div style={{ display: "grid", gap: 2, marginTop: 8 }}>
        {isAdminView && (
          <AccountItem
            icon={<Settings size={17} />}
            label="Settings"
            hint="Users, MFA, logs, recording storage, single sign-on"
            onClick={() => navigate("/pam/settings")}
          />
        )}
        {isAdmin && (
          <AccountItem
            icon={<Repeat size={17} />}
            label={`Switch to ${isAdminView ? "End-user" : "Admin"} view`}
            onClick={handleToggle}
          />
        )}
      </div>
      <div style={{ height: 1, background: AN_COLORS.line, margin: "8px 4px" }} />
      <AccountItem icon={<LogOut size={17} />} label="Log out" onClick={confirmLogout} danger />
    </div>
  );

  return (
    <AntHeader
      className="header-container"
      style={{
        position: "sticky",
        top: 0,
        zIndex: 10,
        width: "100%",
        display: "flex",
        alignItems: "center",
        gap: 16,
        padding: "0 20px",
        background: "var(--an-surface)",
        color: AN_COLORS.ink,
        height: 56,
        lineHeight: 1,
        borderBottom: `1px solid ${AN_COLORS.line}`,
        fontFamily: AN_FONT,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          paddingRight: 20,
          borderRight: `1px solid ${AN_COLORS.line}`,
          height: 32,
        }}
      >
        <img
          src={themeMode === "dark" ? logoDarkPng : logoPng}
          alt="AuthNull"
          style={{ height: 28, width: "auto", display: "block" }}
        />
      </div>

      {/*
        Dummy tenant pill — commented out until tenant/environment data is real.
        <button style={{...}}>
          <span>Acme Corp</span> <span>Production</span>
        </button>
      */}

      {/* Spacer: pushes the account menu to the right. */}
      <div style={{ flex: 1 }} />

      {/*
        Dummy icon buttons — uncomment when help/notifications are wired.
        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
          <IconButton><HelpCircle size={16} /></IconButton>
          <IconButton badge><Bell size={16} /></IconButton>
        </div>
      */}

      <div
        style={{
          display: "flex",
          alignItems: "center",
          paddingLeft: 12,
          borderLeft: `1px solid ${AN_COLORS.line}`,
        }}
      >
        <Dropdown
          open={accountOpen}
          onOpenChange={setAccountOpen}
          popupRender={() => accountPanel}
          trigger={["click"]}
          placement="bottomRight"
        >
          <button
            type="button"
            title="Account"
            className={`an-account-trigger${accountOpen ? " is-open" : ""}`}
            aria-haspopup="menu"
            aria-expanded={accountOpen}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              padding: "4px 10px 4px 4px",
              background: accountOpen ? AN_COLORS.bg : "transparent",
              border: `1px solid ${accountOpen ? AN_COLORS.line : "transparent"}`,
              borderRadius: 999,
              cursor: "pointer",
              fontFamily: AN_FONT,
              transition: "background 120ms ease, border-color 120ms ease",
            }}
          >
            {renderAvatar(30)}
            <span style={{ display: "grid", textAlign: "left", lineHeight: 1.15, minWidth: 0 }}>
              <span
                style={{
                  fontSize: 13,
                  color: AN_COLORS.ink,
                  fontWeight: 600,
                  maxWidth: 140,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {displayName}
              </span>
              <span style={{ fontSize: 11, color: AN_COLORS.muted }}>{roleLabel}</span>
            </span>
            <ChevronDown
              size={14}
              color={AN_COLORS.muted}
              style={{ transition: "transform 150ms ease", transform: accountOpen ? "rotate(180deg)" : "none" }}
            />
          </button>
        </Dropdown>
      </div>
    </AntHeader>
  );
}

export default Header;
